import React from 'react';
import { Link } from 'react-router-dom';
import { FiInfo, FiX, FiArrowRight } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

interface GuestModeBannerProps {
  onDismiss?: () => void;
}

const GuestModeBanner: React.FC<GuestModeBannerProps> = ({ onDismiss }) => {
  const { isGuest, usage } = useAuth();

  if (!isGuest || !usage) return null;

  const percentUsed = (usage.linksCreated / usage.linksLimit) * 100;
  const isNearLimit = usage.remainingLinks <= 1;
  const isAtLimit = usage.remainingLinks === 0;

  return (
    <div className={`relative ${
      isAtLimit ? 'bg-red-50 border-red-200' : 
      isNearLimit ? 'bg-yellow-50 border-yellow-200' : 
      'bg-blue-50 border-blue-200'
    } border rounded-lg p-4 mb-6`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <FiInfo className={`h-5 w-5 ${
            isAtLimit ? 'text-red-400' : 
            isNearLimit ? 'text-yellow-400' : 
            'text-blue-400'
          }`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${
            isAtLimit ? 'text-red-800' : 
            isNearLimit ? 'text-yellow-800' : 
            'text-blue-800'
          }`}>
            {isAtLimit ? 'Guest limit reached!' : 
             isNearLimit ? 'Almost at your guest limit!' : 
             'You\'re using AnonLink as a guest'}
          </h3>
          <div className={`mt-2 text-sm ${
            isAtLimit ? 'text-red-700' : 
            isNearLimit ? 'text-yellow-700' : 
            'text-blue-700'
          }`}>
            <p>
              {isAtLimit ? 
                'You\'ve used all 3 free guest links. Create an account to continue shortening URLs.' :
                `You've used ${usage.linksCreated} of ${usage.linksLimit} free guest links.`
              }
            </p>
            
            {/* Progress bar */}
            {!isAtLimit && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>Links used</span>
                  <span className="font-medium">{usage.linksCreated} / {usage.linksLimit}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${
                      isNearLimit ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${percentUsed}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Call to action */}
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  isAtLimit ? 'bg-red-600 hover:bg-red-700' : 
                  isNearLimit ? 'bg-yellow-600 hover:bg-yellow-700' : 
                  'bg-primary-600 hover:bg-primary-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
              >
                Create Free Account
                <FiArrowRight className="ml-2 h-4 w-4" />
              </Link>
              
              {!isAtLimit && (
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  View Plans
                </Link>
              )}
            </div>

            {/* Benefits list */}
            <div className="mt-4 text-xs">
              <p className="font-medium mb-1">With a free account:</p>
              <ul className="space-y-0.5">
                <li>• 10 links per month</li>
                <li>• Save and manage all your links</li>
                <li>• Detailed analytics</li>
                <li>• Links expire in max 30 days</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Dismiss button */}
        {onDismiss && !isAtLimit && (
          <button
            onClick={onDismiss}
            className="ml-3 flex-shrink-0"
          >
            <FiX className={`h-5 w-5 ${
              isNearLimit ? 'text-yellow-400 hover:text-yellow-500' : 
              'text-blue-400 hover:text-blue-500'
            }`} />
          </button>
        )}
      </div>
    </div>
  );
};

export default GuestModeBanner;